/* eslint-disable react/no-unescaped-entities -- guide text uses many apostrophes/quotes that are fine for rendered Italian copy */
import { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
    BookOpen,
    Download,
    ShieldCheck,
    KeyRound,
    Upload,
    UserCog,
    Mail,
    Phone,
    Sparkles,
    CheckCircle2,
    Wallet,
    CalendarClock,
    Plane,
    Clock4,
    MapPinned,
} from "lucide-react";

const SECTIONS = [
    {
        id: "download",
        icon: Download,
        title: "Scaricare i tuoi documenti",
        body: (
            <>
                <p>
                    Nella sezione principale del portale trovi tutti i documenti che l'azienda ha
                    caricato per te: <strong>buste paga, CU/730, contratti, certificati, comunicazioni,
                    visite mediche</strong> e altro.
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li>Clicca sulle <strong>card colorate</strong> in alto per filtrare per categoria.</li>
                    <li>Usa la barra di <strong>ricerca</strong> per trovare un documento per titolo o periodo.</li>
                    <li>Clicca sul <strong>nome del file</strong> per l'anteprima: puoi <strong>scorrere tutte le pagine</strong> e ingrandire con i pulsanti <strong>zoom +/−</strong>.</li>
                    <li>Clicca su <strong>"Scarica"</strong> per salvare il file sul tuo dispositivo.</li>
                </ul>
                <p>
                    Se l'azienda ha pubblicato un documento importante lo trovi nella card ambra
                    <strong> "In evidenza"</strong> in cima alla pagina.
                </p>
            </>
        ),
    },
    {
        id: "ack",
        icon: ShieldCheck,
        title: "Documenti con presa visione",
        body: (
            <>
                <p>
                    Alcuni documenti (es. <strong>comunicazioni ufficiali</strong> o <strong>contratti</strong>)
                    richiedono la tua <strong>presa visione</strong>: vedrai un badge ambra accanto al titolo
                    e un <strong>banner blu</strong> in cima alla pagina con l'elenco di quelli in attesa.
                </p>
                <p>
                    Al primo download apparirà una finestra che ti chiede di confermare di aver letto e
                    compreso il contenuto. Se hai più documenti in attesa, dal banner puoi usare
                    <strong> "Conferma tutte"</strong> per firmarli in un'unica operazione.
                </p>
                <p>
                    La conferma viene registrata con <strong>data, ora e indirizzo IP</strong>
                    {" "}e ha valore legale ai sensi del Reg. UE 910/2014 (eIDAS) art. 25.
                </p>
            </>
        ),
    },
    {
        id: "expiry",
        icon: CalendarClock,
        title: "Documenti in scadenza",
        body: (
            <>
                <p>
                    Se un tuo documento personale (es. <strong>carta d'identità, certificati, attestati</strong>)
                    è <strong>scaduto o in scadenza</strong>, in cima al portale compare un banner di avviso
                    con l'elenco dei documenti interessati.
                </p>
                <p>
                    Quando rinnovi il documento, invialo all'azienda con il pulsante
                    <strong> "Invia documento"</strong>: l'ufficio HR aggiornerà il tuo fascicolo.
                </p>
            </>
        ),
    },
    {
        id: "acconti",
        icon: Wallet,
        title: "Acconti e prestiti",
        body: (
            <>
                <p>
                    Cliccando sulla card verde <strong>"Acconti"</strong> trovi la tua situazione:
                    acconti e prestiti in corso, trattenute già effettuate e le <strong>ricevute</strong> da
                    scaricare o visualizzare in anteprima.
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li>Con <strong>"Richiedi acconto"</strong> puoi inviare una nuova richiesta: l'ufficio HR la riceverà e ti contatterà per l'approvazione.</li>
                    <li>In <strong>"Le mie richieste"</strong> controlli lo stato delle richieste inviate.</li>
                </ul>
            </>
        ),
    },
    {
        id: "richieste",
        icon: Plane,
        title: "Ferie, permessi e straordinari",
        body: (
            <>
                <p>
                    Dalla pagina <strong>"Richieste"</strong> puoi inviare all'ufficio:
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li><strong>Ferie e permessi</strong>: scegli le date (e l'orario per i permessi) e invia la richiesta.</li>
                    <li><strong>Straordinari</strong>: comunichi le ore extra indicando data, ore e il <strong>cantiere/cliente</strong> dove hai lavorato.</li>
                </ul>
                <p>
                    Nella stessa pagina segui lo <strong>stato</strong> di ogni richiesta (in attesa,
                    approvata o rifiutata).
                </p>
            </>
        ),
    },
    {
        id: "ore",
        icon: Clock4,
        title: "Le mie ore",
        body: (
            <>
                <p>
                    Nella pagina <strong>"Le mie ore"</strong> trovi il riepilogo mensile delle tue
                    timbrature: totale ore, giorni lavorati, straordinari e ferie.
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li>Con il selettore <strong>Elenco / Calendario</strong> scegli come visualizzarle.</li>
                    <li>La vista <strong>Calendario</strong> mostra tutto il mese con le ore timbrate in ogni giorno; toccando un giorno vedi entrate, uscite e cantiere.</li>
                </ul>
            </>
        ),
    },
    {
        id: "timbratura",
        icon: MapPinned,
        title: "Timbratura (se abilitato)",
        body: (
            <>
                <p>
                    Se sei abilitato alla timbratura geolocalizzata, dal portale passi al modulo con il
                    pulsante <strong>"Timbratura"</strong> (su smartphone lo trovi nel <strong>menu</strong> in
                    alto a destra).
                </p>
                <p>
                    Dal modulo Timbratura torni all'area personale con il pulsante <strong>"Portale"</strong>.
                    Le istruzioni complete sono nella <strong>"Guida alla Timbratura"</strong> tra i tuoi documenti.
                </p>
            </>
        ),
    },
    {
        id: "password",
        icon: KeyRound,
        title: "Cambiare la password",
        body: (
            <>
                <p>
                    Per modificare la tua password di accesso:
                </p>
                <ol className="ml-4 list-decimal space-y-1">
                    <li>Clicca sul pulsante <strong>"Profilo"</strong> in alto a destra.</li>
                    <li>Seleziona la scheda <strong>"Password"</strong>.</li>
                    <li>Inserisci la <strong>password attuale</strong> e la <strong>nuova password</strong> (minimo 6 caratteri), poi conferma.</li>
                </ol>
                <p>
                    Se hai dimenticato la password, dalla schermata di login clicca su
                    <strong> "Password dimenticata?"</strong> e segui le istruzioni che riceverai via email.
                </p>
            </>
        ),
    },
    {
        id: "upload",
        icon: Upload,
        title: "Inviare un documento all'azienda",
        body: (
            <>
                <p>
                    Puoi inviare documenti all'ufficio HR (es. <strong>richieste ferie, certificati medici,
                    comunicazioni personali</strong>) direttamente dal portale.
                </p>
                <ol className="ml-4 list-decimal space-y-1">
                    <li>Clicca sul pulsante <strong>"Invia documento"</strong> in alto a destra.</li>
                    <li>Inserisci un <strong>titolo</strong> e, se vuoi, una breve descrizione.</li>
                    <li>Seleziona il file dal tuo dispositivo e clicca <strong>"Invia"</strong>.</li>
                </ol>
                <p>
                    Puoi controllare lo stato dei tuoi invii con il pulsante <strong>"I miei invii"</strong> in
                    alto a destra: vedrai se il documento è stato letto dall'azienda.
                </p>
            </>
        ),
    },
    {
        id: "data",
        icon: UserCog,
        title: "Aggiornare i tuoi dati di contatto",
        body: (
            <>
                <p>
                    Puoi richiedere l'aggiornamento dei tuoi dati di contatto e residenza
                    (<strong>email, telefono, indirizzo</strong>) direttamente dal portale.
                </p>
                <ul className="ml-4 list-disc space-y-1">
                    <li>Clicca su <strong>"Profilo"</strong> in alto a destra e resta nella scheda <strong>"I miei dati"</strong>.</li>
                    <li>Modifica i campi e invia la richiesta.</li>
                    <li>L'ufficio HR riceverà una notifica e validerà le modifiche.</li>
                    <li>Se cambi residenza, ricorda di inviare anche il <strong>documento ufficiale</strong> con "Invia documento".</li>
                </ul>
            </>
        ),
    },
    {
        id: "support",
        icon: Mail,
        title: "Hai bisogno di aiuto?",
        body: (
            <>
                <p>Per qualsiasi richiesta puoi contattare direttamente l'ufficio HR:</p>
                <ul className="ml-4 list-none space-y-1">
                    <li className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-[#376DB3]" />
                        <a href="mailto:dipendenti@splendorsud.com" className="font-medium text-[#376DB3] hover:underline">
                            dipendenti@splendorsud.com
                        </a>
                    </li>
                    <li className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-[#376DB3]" />
                        <a href="tel:+39099375014" className="font-medium text-[#376DB3] hover:underline">
                            099.375014
                        </a>{" "}
                        <span className="text-slate-500">(ufficio)</span>
                    </li>
                    <li className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-[#376DB3]" />
                        <a href="tel:+393481966463" className="font-medium text-[#376DB3] hover:underline">
                            348.1966463
                        </a>{" "}
                        <span className="text-slate-500">(cellulare)</span>
                    </li>
                </ul>
            </>
        ),
    },
];

const STORAGE_KEY_PREFIX = "ss_employee_guide_seen_";

export function hasSeenGuide(userId) {
    if (!userId) return true;
    try {
        return localStorage.getItem(STORAGE_KEY_PREFIX + userId) === "1";
    } catch {
        return true;
    }
}

export function markGuideSeen(userId) {
    if (!userId) return;
    try {
        localStorage.setItem(STORAGE_KEY_PREFIX + userId, "1");
    } catch {
        /* ignore */
    }
}

export function EmployeeGuideDialog({ open, onClose, userId, isFirstTime = false, focusSectionId = null }) {
    const [acknowledged, setAcknowledged] = useState(false);
    const sectionRefs = useRef({});
    const scrollContainerRef = useRef(null);

    // When the dialog opens with a target section, scroll to it and apply a
    // transient highlight via plain DOM (uncoupled from React reconciler).
    useEffect(() => {
        if (!open || !focusSectionId) return;
        const t = setTimeout(() => {
            const node = sectionRefs.current[focusSectionId];
            const container = scrollContainerRef.current;
            if (!node || !container) return;
            const nodeRect = node.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            container.scrollTop += nodeRect.top - containerRect.top - 12;
            node.classList.add("guide-section-flash");
            setTimeout(() => node.classList.remove("guide-section-flash"), 1900);
        }, 50);
        return () => clearTimeout(t);
    }, [open, focusSectionId]);

    const handleClose = () => {
        if (userId) markGuideSeen(userId);
        setAcknowledged(false);  // reset the checkbox so next open starts fresh
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent
                className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0"
                data-testid="employee-guide-dialog"
            >
                {/* Header with brand gradient */}
                <DialogHeader className="flex-shrink-0 bg-gradient-to-br from-[#376DB3] to-[#5BAEDB] px-6 py-5 text-white">
                    <DialogTitle className="flex items-center gap-3 text-white">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20 backdrop-blur">
                            <BookOpen className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="font-display text-xl font-bold">
                                {isFirstTime ? "Benvenuto nel Portale HR" : "Guida all'uso del portale"}
                            </div>
                            <div className="text-xs font-normal text-white/80">
                                {isFirstTime
                                    ? "Ecco come muoverti tra le funzioni principali"
                                    : "Tutte le funzioni del tuo portale personale"}
                            </div>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                {/* Scrollable body */}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5" ref={scrollContainerRef}>
                    {isFirstTime && (
                        <div className="mb-5 flex items-start gap-3 rounded-lg border border-[#376DB3]/20 bg-[#5BAEDB]/10 p-3">
                            <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#376DB3]" />
                            <p className="text-sm text-slate-700">
                                Questa guida ti aiuta a familiarizzare con il portale. Potrai sempre
                                riaprirla cliccando sul pulsante <strong>"Guida"</strong> in alto a destra.
                            </p>
                        </div>
                    )}

                    <div className="space-y-5">
                        {SECTIONS.map((sec, idx) => {
                            const Icon = sec.icon;
                            return (
                                <section
                                    key={sec.id}
                                    ref={(el) => { sectionRefs.current[sec.id] = el; }}
                                    className="relative pl-12"
                                    data-testid={`guide-section-${sec.id}`}
                                >
                                    <div className="absolute left-0 top-0 flex h-9 w-9 items-center justify-center rounded-lg bg-[#376DB3] text-white shadow-sm">
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <h3 className="font-display text-base font-bold text-[#376DB3]">
                                        {idx + 1}. {sec.title}
                                    </h3>
                                    <div className="mt-1.5 space-y-2 text-sm leading-relaxed text-slate-700">
                                        {sec.body}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <DialogFooter className="flex-shrink-0 gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:justify-between">
                    {isFirstTime ? (
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                            <input
                                type="checkbox"
                                checked={acknowledged}
                                onChange={(e) => setAcknowledged(e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300 text-[#376DB3] focus:ring-[#376DB3]"
                                data-testid="guide-ack-checkbox"
                            />
                            <span>Ho letto la guida</span>
                        </label>
                    ) : (
                        <div className="text-xs text-slate-500">
                            Trovi questa guida sempre disponibile nel pulsante "Guida".
                        </div>
                    )}
                    <Button
                        type="button"
                        onClick={handleClose}
                        disabled={isFirstTime && !acknowledged}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="guide-close-btn"
                    >
                        <CheckCircle2 className="h-4 w-4" />
                        {isFirstTime ? "Inizia a usare il portale" : "Chiudi"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
